import { useCallback, useEffect, useRef, useState } from 'react';
import { pausedMsSince } from '../talk/talkOpen';
import { api } from '../api/client';
import { REWARD_ICONS, useSettings, type RewardIcon } from '../settings/SettingsContext';
import { canSpeak, speak, stopSpeaking } from './speak';
import { playCorrectPing } from './ping';
import { endSession, fetchRewardSummary, type RewardSummary } from '../rewards/rewards';
import { registerTrial, respondTrial } from './trials';
import type { ProgramView } from '../programs/types';

type Prompt = { prompt: string; hint?: string };

type Props = {
  programId: string;
  learnerId?: string;
};

/* HELD OR STOPPED, AND BOTH ARE WORTH RECORDING. A hold that was let go is not a
   failure to be hidden — it is the measurement. */
const HELD = 'HELD';
const STOPPED = 'STOPPED';

export default function TimerHold({ programId, learnerId }: Props) {
  const { settings } = useSettings();
  const [prompts, setPrompts] = useState<Prompt[] | null>(null);
  const [holdSeconds, setHoldSeconds] = useState(5);
  const [current, setCurrent] = useState<Prompt | null>(null);
  const [trialId, setTrialId] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(5);
  const [holding, setHolding] = useState(false);
  const [outcome, setOutcome] = useState<'HELD' | 'STOPPED' | null>(null);
  const [held, setHeld] = useState(0);
  const [letGo, setLetGo] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [collected, setCollected] = useState<RewardSummary | null>(null);
  const [earnedNow, setEarnedNow] = useState(0);
  const tickRef = useRef<number | null>(null);
  /** The trial already settled, so a second attempt to settle it is dropped. */
  const settledRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const answeredRef = useRef(false);

  function clearTick() {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await api<ProgramView>(`/programs/${programId}`, { token: true });
        if (cancelled) return;
        const raw = (p.config?.prompts ?? []) as Prompt[];
        let usable = raw.filter((x) => x && typeof x.prompt === 'string' && x.prompt.trim());
        /* THE CONCEPTS CARRY A SINGLE promptText RATHER THAN A LIST, so a program
           built from one still has something to hold. */
        if (usable.length === 0 && typeof p.config?.promptText === 'string') {
          usable = [{ prompt: p.config.promptText as string }];
        }
        setPrompts(usable);
        const secs = Number(p.config?.holdSeconds);
        setHoldSeconds(Number.isFinite(secs) && secs > 0 ? Math.min(120, secs) : 5);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load the program');
      }
    })();
    return () => { cancelled = true; };
  }, [programId]);

  useEffect(() => { setRemaining(holdSeconds); }, [holdSeconds]);

  useEffect(() => {
    if (!settings.features.rewardTotal) return;
    let cancelled = false;
    void fetchRewardSummary(learnerId ?? null).then((s) => {
      if (!cancelled) setCollected(s);
    });
    return () => { cancelled = true; };
  }, [learnerId, settings.features.rewardTotal]);

  useEffect(() => () => { stopSpeaking(); clearTick(); }, []);

  useEffect(() => {
    const session = sessionIdRef.current;
    return () => {
      void endSession(programId, session, answeredRef.current);
    };
  }, [programId]);

  const settle = useCallback(async (id: string, how: 'HELD' | 'STOPPED') => {
    /* ONE VERDICT PER TRIAL. Two settles raced each other here — both read the
       round before either saved, so both passed the server's already-complete
       guard and the hold counted twice. */
    if (settledRef.current === id) return;
    settledRef.current = id;
    try {
      const verdict = await respondTrial(id, how);
      answeredRef.current = true;
      if (verdict.rewarded > 0) setEarnedNow((n) => n + verdict.rewarded);
      if (how === HELD) {
        setHeld((n) => n + 1);
        if (settings.correctPingEnabled) playCorrectPing();
      } else {
        setLetGo((n) => n + 1);
      }
      setOutcome(how);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record that hold');
    }
  }, [settings.correctPingEnabled]);

  async function start() {
    if (holding || !prompts || prompts.length === 0) return;
    setError(null);
    setOutcome(null);
    const chosen = prompts[Math.floor(Math.random() * prompts.length)];

    let id: string;
    try {
      const registered = await registerTrial(
        programId,
        learnerId,
        { prompt: chosen.prompt, holdSeconds },
        { value: HELD },
      );
      id = registered.trialId;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the hold');
      return;
    }

    setCurrent(chosen);
    setTrialId(id);
    setRemaining(holdSeconds);
    setHolding(true);
    if (canSpeak()) {
      speak(chosen.prompt, { voiceName: settings.ttsVoice, rate: settings.ttsRate });
    }

    /* NOTHING HAPPENS INSIDE THE STATE UPDATER. Clearing the timer, ending the
       hold and settling the trial were all done inside setRemaining's updater,
       which React is free to call more than once — and does, to surface exactly
       this. Counted from a deadline instead, so the work happens once per tick
       and the remaining time cannot drift. */
    const startedAt = performance.now();
    tickRef.current = window.setInterval(() => {
      const held = performance.now() - startedAt - pausedMsSince(startedAt);
      const left = Math.max(0, Math.ceil((holdSeconds * 1000 - held) / 1000));
      setRemaining(left);
      if (left <= 0) {
        clearTick();
        setHolding(false);
        void settle(id, HELD);
      }
    }, 250);
  }

  function stop() {
    if (!holding || !trialId) return;
    clearTick();
    setHolding(false);
    stopSpeaking();
    void settle(trialId, STOPPED);
    setRemaining(holdSeconds);
  }

  if (error) {
    return (
      <div className="card">
        <div className="error" role="alert">{error}</div>
      </div>
    );
  }
  if (prompts && prompts.length === 0) {
    return (
      <div className="card">
        <p>This program has nothing to hold yet.</p>
        <p className="field-hint">Edit it to add what the learner should hold.</p>
      </div>
    );
  }
  if (!prompts) return null;

  const display = holding ? remaining : holdSeconds;

  return (
    <>
      {settings.features.rewardTotal && collected?.enabled && (
        <div className="board-total">
          <span aria-hidden="true">
            {REWARD_ICONS[collected.icon as RewardIcon] ?? REWARD_ICONS.star}
          </span>
          <strong>{collected.total + earnedNow}</strong>
          <span>{collected.name}</span>
        </div>
      )}

      <div className="card concept-timer">
        <div className="muted">✓ {held}{' · '}✗ {letGo}</div>

        <div className="concept-prompt-big">{current ? current.prompt : 'Ready'}</div>
        {current?.hint && !holding && (
          <div className="muted" style={{ marginBottom: 12 }}>{current.hint}</div>
        )}

        <div className="concept-timer-display" aria-live="polite">{display}s</div>

        {outcome === HELD && <div className="muted">Held for the whole time.</div>}
        {outcome === STOPPED && <div className="muted">Let go early — written down as it happened.</div>}

        <div className="toolbar" style={{ justifyContent: 'center' }}>
          {!holding ? (
            <button className="btn" onClick={() => void start()} aria-label="Start the hold">
              {outcome ? 'Go again' : 'Start'}
            </button>
          ) : (
            <button className="btn secondary" onClick={stop} aria-label="Stop the hold">
              Stop
            </button>
          )}
        </div>
      </div>
    </>
  );
}
